import { useEffect, useState } from 'react'
import { Menu, MenuItem } from '@mui/material'
import type { MenuSchedule } from '@/shared/api/location'
import { formatTimeStr, getScheduleHourForDisplay } from '@/lib/timeUtils'
import { typography } from '@/styles/designTokens'
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined'
import MenuBookTwoToneIcon from '@mui/icons-material/MenuBookTwoTone'
import {
	PillRoot,
	PillLabel,
	PillLabelBold,
	PillLabelNormal,
	PillIconWrap,
	dropdownItemSx,
	dropdownItemOffHoursSx,
	dropdownPaperSx,
} from './MenuSchedulePill.styled'

interface MenuSchedulePillProps {
	schedule: MenuSchedule
	schedules: MenuSchedule[]
	onSelect: (schedule: MenuSchedule) => void
	/**
	 * STO-3834: greys off-hours options, matching Ember's dine-in-only treatment
	 * (category-tabs.hbs:42). Other order types either filter them out already (ASAP) or
	 * validate against a future time instead of now (deferred).
	 */
	isDineIn?: boolean
	fullWidth?: boolean
	compact?: boolean
}

export function MenuSchedulePill({
	schedule,
	schedules,
	onSelect,
	isDineIn = false,
	fullWidth,
	compact,
}: MenuSchedulePillProps) {
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
	const open = Boolean(anchorEl)
	const isInteractive = schedules.length > 1

	// React-idiomatic derived-state pattern (setState during render, not in an effect):
	// when the schedule list shrinks to one while mounted, clear anchorEl so the menu
	// cannot reopen automatically if the list later grows back to two entries.
	// See: https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
	const [prevSchedulesLength, setPrevSchedulesLength] = useState(schedules.length)
	if (schedules.length !== prevSchedulesLength) {
		setPrevSchedulesLength(schedules.length)
		if (schedules.length <= 1 && anchorEl !== null) {
			setAnchorEl(null)
		}
	}

	useEffect(() => {
		if (!open) return
		const onScroll = () => setAnchorEl(null)
		window.addEventListener('scroll', onScroll, { passive: true })
		return () => window.removeEventListener('scroll', onScroll)
	}, [open])

	// STO-3834: must match the dropdown options below. A now-only lookup returns null for an
	// off-hours schedule, silently dropping the time range from the label.
	const displayHour = getScheduleHourForDisplay(schedule)
	const timeRange = displayHour
		? `${formatTimeStr(displayHour.startTime)} – ${formatTimeStr(displayHour.endTime)}`
		: null

	if (schedules.length <= 1) return null

	function handleOpen(e: React.MouseEvent<HTMLElement>) {
		if (isInteractive) setAnchorEl(e.currentTarget)
	}

	function handleClose() {
		setAnchorEl(null)
	}

	function handleSelect(s: MenuSchedule) {
		onSelect(s)
		setAnchorEl(null)
	}

	return (
		<>
			<PillRoot
				onClick={handleOpen}
				data-testid="menu-schedule-pill"
				isInteractive={isInteractive}
				fullWidth={fullWidth}
				compact={compact}
				aria-haspopup={isInteractive ? 'menu' : undefined}
				aria-expanded={open || undefined}
				aria-disabled={!isInteractive || undefined}
			>
				<MenuBookTwoToneIcon
					sx={{ fontSize: typography.fontSizeIconMd }}
					aria-hidden="true"
				/>
				<PillLabel>
					<PillLabelBold>{schedule.name}</PillLabelBold>
					{timeRange && <PillLabelNormal>{timeRange}</PillLabelNormal>}
				</PillLabel>
				{isInteractive && (
					<PillIconWrap>
						<ExpandMoreOutlinedIcon
							sx={{ fontSize: typography.fontSizeLg }}
							aria-hidden="true"
						/>
					</PillIconWrap>
				)}
			</PillRoot>

			<Menu
				anchorEl={anchorEl}
				open={open}
				onClose={handleClose}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
				transformOrigin={{ vertical: 'top', horizontal: 'left' }}
				slotProps={{ paper: { sx: dropdownPaperSx } }}
				disableScrollLock
			>
				{schedules.map((s) => {
					const itemHour = getScheduleHourForDisplay(s)
					const itemTimeRange = itemHour
						? `${formatTimeStr(itemHour.startTime)} – ${formatTimeStr(itemHour.endTime)}`
						: null
					// Ember parity (category-tabs.hbs:42): the greyed treatment and aria-disabled sit
					// on the inner label span, not on the interactive option — the option stays
					// selectable because the venue has opted into ordering outside schedule hours.
					// aria-disabled must not go on the MenuItem itself: that declares the control
					// inert to assistive tech while onClick still fires (WCAG 2.1 SC 4.1.2).
					const isOffHours = isDineIn && s.duringOffHours
					return (
						<MenuItem
							key={s.ref}
							selected={s.ref === schedule.ref}
							onClick={() => handleSelect(s)}
							sx={isOffHours ? dropdownItemOffHoursSx : dropdownItemSx}
						>
							<PillLabelBold aria-disabled={isOffHours || undefined}>
								{s.name}
							</PillLabelBold>
							{itemTimeRange && <PillLabelNormal> ({itemTimeRange})</PillLabelNormal>}
						</MenuItem>
					)
				})}
			</Menu>
		</>
	)
}
